"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, CircleCheck as CheckCircle2, MapPin, Shield, Zap, X } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
      if (saved && ["es", "en", "eu", "fr"].includes(saved)) {
        return saved;
      }
    } catch {
      // ignore
    }
    return "es";
  });
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue) {
        if (["es", "en", "eu", "fr"].includes(e.newValue)) {
          setLanguageState(e.newValue as Language);
        }
      }
    };
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<Language>;
      if (customEvent.detail && ["es", "en", "eu", "fr"].includes(customEvent.detail)) {
        setLanguageState(customEvent.detail);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(LANGUAGE_EVENT_NAME, handleCustomChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleCustomChange);
    };
  }, []);
  const setLanguage = (lang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: lang
      }));
      setLanguageState(lang);
    } catch {
      // ignore
    }
  };
  return [language, setLanguage];
}
interface ProjectItem {
  id: string;
  categoryKey: "led" | "control" | "reforma" | "instalacion";
  year: string;
  images: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  }[];
}
const PROJECTS: ProjectItem[] = [{
  id: "led-indirecto",
  categoryKey: "led",
  year: "2023",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg-wvc-sizes",
    alt: "Iluminación LED indirecta en techo"
  }]
}, {
  id: "cuadro-metasys",
  categoryKey: "control",
  year: "2022",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-sizes",
    alt: "Cuadro de control y automatización Metasys"
  }]
}, {
  id: "reforma-integral",
  categoryKey: "reforma",
  year: "2023",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg-wvc-sizes",
    alt: "Reforma eléctrica integral de vivienda"
  }]
}, {
  id: "recableado-portal",
  categoryKey: "instalacion",
  year: "2022",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1439003.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1439003.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1439003.jpg-wvc-sizes",
    alt: "Apertura de rozas e instalación de tubos corrugados en reforma eléctrica de vivienda"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1805011.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1805011.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260625_1805011.jpg-wvc-sizes",
    alt: "Instalación y nivelación de caja de distribución eléctrica empotrada en pared de ladrillo durante reforma"
  }]
}, {
  id: "reforma-integral-vivienda-2025",
  categoryKey: "reforma",
  year: "2025",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250612_164851.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250612_164851.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250612_164851.jpg-wvc-sizes",
    alt: "Instalación de cuadro eléctrico y caja de distribución en vivienda"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250704_115808.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250704_115808.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250704_115808.jpg-wvc-sizes",
    alt: "Cableado de techo para instalación de focos empotrados en reforma eléctrica"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250723_141632.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250723_141632.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG_20250723_141632.jpg-wvc-sizes",
    alt: "Resultado final de reforma eléctrica con cuadro, contador y focos LED instalados"
  }]
}, {
  id: "renovacion-cuadro-electrico-2026",
  categoryKey: "control",
  year: "2026",
  images: [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_111848.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_111848.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_111848.jpg-wvc-sizes",
    alt: "Acceso al cuadro eléctrico general con contador y tapa frontal retirada durante renovación"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_113739.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_113739.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260813_113739.jpg-wvc-sizes",
    alt: "Cableado expuesto junto al contador eléctrico durante la renovación del cuadro"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_160121.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_160121.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_160121.jpg-wvc-sizes",
    alt: "Instalación y etiquetado de nuevos automáticos en el cuadro eléctrico"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_172004.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_172004.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260818_172004.jpg-wvc-sizes",
    alt: "Resultado final del cuadro eléctrico renovado con canaleta y acabado en vivienda"
  }]
}];
const LOCATIONS = ["Bilbao", "Getxo", "Neguri", "Abando", "Indautxu"];
const translations = {
  es: {
    eyebrow: "Somos Electro & Más",
    headingPart1: "Hacer el trabajo bien.",
    headingPart2: "Que el cliente quede satisfecho.",
    categories: {
      all: "Todos",
      led: "Iluminación LED",
      control: "Cuadros & Control",
      reforma: "Reforma Integral",
      instalacion: "Instalación"
    },
    bannerYearsLabel: "Años electrificando",
    bannerTitle: "Bilbao y el País Vasco",
    bannerParagraph: "Instalaciones certificadas, especialistas en domótica Loxone y reformas eléctricas integrales.",
    bannerBadge1: "Garantía Oficial",
    bannerBadge2: "Certificado Autorizado",
    cardGuarantee: "Ejecución técnica garantizada",
    cardDetails: "Ver detalle",
    ctaWhatsapp: "Consultar por WhatsApp (662 49 88 28)",
    ctaQuote: "Solicitar presupuesto de reforma",
    ariaPrev: "Imagen anterior",
    ariaNext: "Siguiente imagen",
    ariaClose: "Cerrar vista ampliada",
    projects: {
      "led-indirecto": {
        title: "Iluminación LED indirecta en techo",
        category: "Iluminación LED"
      },
      "cuadro-metasys": {
        title: "Cuadro de control y automatización Metasys",
        category: "Cuadros & Control"
      },
      "reforma-integral": {
        title: "Reforma eléctrica integral de vivienda",
        category: "Reforma Integral"
      },
      "recableado-portal": {
        title: "Recableado completo de portal y vivienda",
        category: "Instalación"
      },
      "reforma-integral-vivienda-2025": {
        title: "Reforma eléctrica integral de vivienda",
        category: "Reforma Integral"
      },
      "renovacion-cuadro-electrico-2026": {
        title: "Renovación de cuadro eléctrico general en vivienda",
        category: "Cuadros & Control"
      }
    }
  },
  en: {
    eyebrow: "We Are Electro & Más",
    headingPart1: "Doing the job right.",
    headingPart2: "Leaving the client satisfied.",
    categories: {
      all: "All",
      led: "LED Lighting",
      control: "Control Panels & Automation",
      reforma: "Complete Renovation",
      instalacion: "Installation"
    },
    bannerYearsLabel: "Years electrifying",
    bannerTitle: "Bilbao and the Basque Country",
    bannerParagraph: "Certified installations, Loxone home automation specialists and comprehensive electrical renovations.",
    bannerBadge1: "Official Warranty",
    bannerBadge2: "Authorized Certificate",
    cardGuarantee: "Guaranteed technical execution",
    cardDetails: "View details",
    ctaWhatsapp: "Inquire via WhatsApp (662 49 88 28)",
    ctaQuote: "Request a renovation quote",
    ariaPrev: "Previous image",
    ariaNext: "Next image",
    ariaClose: "Close lightbox",
    projects: {
      "led-indirecto": {
        title: "Indirect ceiling LED lighting",
        category: "LED Lighting"
      },
      "cuadro-metasys": {
        title: "Metasys control and automation panel",
        category: "Control Panels & Automation"
      },
      "reforma-integral": {
        title: "Complete residential electrical renovation",
        category: "Complete Renovation"
      },
      "recableado-portal": {
        title: "Complete building entrance and home rewiring",
        category: "Installation"
      },
      "reforma-integral-vivienda-2025": {
        title: "Complete residential electrical renovation",
        category: "Complete Renovation"
      },
      "renovacion-cuadro-electrico-2026": {
        title: "Main residential electrical panel upgrade",
        category: "Control Panels & Automation"
      }
    }
  },
  eu: {
    eyebrow: "Electro & Más gara",
    headingPart1: "Lana ondo egitea.",
    headingPart2: "Bezeroa pozik geratzea.",
    categories: {
      all: "Guztiak",
      led: "LED Argiztapena",
      control: "Koadroak eta Kontrola",
      reforma: "Erreforma Integrala",
      instalacion: "Instalazioa"
    },
    bannerYearsLabel: "Urteak elektrifikatzen",
    bannerTitle: "Bilbo eta Euskal Herria",
    bannerParagraph: "Instalazio ziurtatuak, Loxone domotikan adituak eta erreforma elektriko integralak.",
    bannerBadge1: "Berme Ofiziala",
    bannerBadge2: "Ziurtagiri Baimendua",
    cardGuarantee: "Gauzapen tekniko bermatua",
    cardDetails: "Xehetasunak ikusi",
    ctaWhatsapp: "WhatsApp bidez galdetu (662 49 88 28)",
    ctaQuote: "Erreforma aurrekontua eskatu",
    ariaPrev: "Aurreko irudia",
    ariaNext: "Hurrengo irudia",
    ariaClose: "Itxi ikuspegi zabaldua",
    projects: {
      "led-indirecto": {
        title: "Zabai-argiztapen LED ez-zuzena",
        category: "LED Argiztapena"
      },
      "cuadro-metasys": {
        title: "Metasys kontrol eta automatizazio koadroa",
        category: "Koadroak eta Kontrola"
      },
      "reforma-integral": {
        title: "Etxebizitzaren erreforma elektriko integrala",
        category: "Erreforma Integrala"
      },
      "recableado-portal": {
        title: "Atariko eta etxebizitzako kableatze osoa",
        category: "Instalazioa"
      },
      "reforma-integral-vivienda-2025": {
        title: "Etxebizitzaren erreforma elektriko integrala",
        category: "Erreforma Integrala"
      },
      "renovacion-cuadro-electrico-2026": {
        title: "Etxebizitzako koadro elektriko orokorraren berrikuntza",
        category: "Koadroak eta Kontrola"
      }
    }
  },
  fr: {
    eyebrow: "Nous sommes Electro & Más",
    headingPart1: "Faire le travail correctement.",
    headingPart2: "Que le client soit satisfait.",
    categories: {
      all: "Tous",
      led: "Éclairage LED",
      control: "Tableaux & Contrôle",
      reforma: "Rénovation Intégrale",
      instalacion: "Installation"
    },
    bannerYearsLabel: "Ans d'électrification",
    bannerTitle: "Bilbao et le Pays Basque",
    bannerParagraph: "Installations certifiées, spécialistes en domotique Loxone et rénovations électriques intégrales.",
    bannerBadge1: "Garantie Officielle",
    bannerBadge2: "Certificat Agréé",
    cardGuarantee: "Exécution technique garantie",
    cardDetails: "Voir les détails",
    ctaWhatsapp: "Consulter par WhatsApp (662 49 88 28)",
    ctaQuote: "Demander un devis de rénovation",
    ariaPrev: "Image précédente",
    ariaNext: "Image suivante",
    ariaClose: "Fermer la vue agrandie",
    projects: {
      "led-indirecto": {
        title: "Éclairage LED indirect au plafond",
        category: "Éclairage LED"
      },
      "cuadro-metasys": {
        title: "Tableau de contrôle et d'automatisation Metasys",
        category: "Tableaux & Contrôle"
      },
      "reforma-integral": {
        title: "Rénovation électrique intégrale d'un logement",
        category: "Rénovation Intégrale"
      },
      "recableado-portal": {
        title: "Recâblage complet d'entrée et de logement",
        category: "Installation"
      },
      "reforma-integral-vivienda-2025": {
        title: "Rénovation électrique intégrale d'un logement",
        category: "Rénovation Intégrale"
      },
      "renovacion-cuadro-electrico-2026": {
        title: "Rénovation de tableau électrique général résidentiel",
        category: "Tableaux & Contrôle"
      }
    }
  }
};
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Trabajos() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeImageIndices, setActiveImageIndices] = useState<Record<string, number>>({});
  const [lightbox, setLightbox] = useState<{
    projectId: string;
    imageIndex: number;
  } | null>(null);
  const categoryFilters: {
    key: string;
    label: string;
  }[] = [{
    key: "all",
    label: t.categories.all
  }, {
    key: "led",
    label: t.categories.led
  }, {
    key: "control",
    label: t.categories.control
  }, {
    key: "reforma",
    label: t.categories.reforma
  }, {
    key: "instalacion",
    label: t.categories.instalacion
  }];
  const filteredProjects = activeCategory === "all" ? PROJECTS : PROJECTS.filter(p => p.categoryKey === activeCategory);
  const lightboxProject = lightbox ? PROJECTS.find(p => p.id === lightbox.projectId) : null;
  const lightboxImage = lightboxProject && lightbox ? lightboxProject.images[lightbox.imageIndex] : null;
  useEffect(() => {
    if (lightbox) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setLightbox(null);
        } else if (e.key === "ArrowLeft") {
          const proj = PROJECTS.find(p => p.id === lightbox.projectId);
          if (proj && proj.images.length > 1) {
            const newIdx = (lightbox.imageIndex - 1 + proj.images.length) % proj.images.length;
            setLightbox({
              projectId: lightbox.projectId,
              imageIndex: newIdx
            });
            setActiveImageIndices(prev => ({
              ...prev,
              [lightbox.projectId]: newIdx
            }));
          }
        } else if (e.key === "ArrowRight") {
          const proj = PROJECTS.find(p => p.id === lightbox.projectId);
          if (proj && proj.images.length > 1) {
            const newIdx = (lightbox.imageIndex + 1) % proj.images.length;
            setLightbox({
              projectId: lightbox.projectId,
              imageIndex: newIdx
            });
            setActiveImageIndices(prev => ({
              ...prev,
              [lightbox.projectId]: newIdx
            }));
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [lightbox]);
  const handlePrevImage = (e: React.MouseEvent, projectId: string, totalImages: number) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveImageIndices(prev => {
      const current = prev[projectId] || 0;
      return {
        ...prev,
        [projectId]: (current - 1 + totalImages) % totalImages
      };
    });
  };
  const handleNextImage = (e: React.MouseEvent, projectId: string, totalImages: number) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveImageIndices(prev => {
      const current = prev[projectId] || 0;
      return {
        ...prev,
        [projectId]: (current + 1) % totalImages
      };
    });
  };
  const handleLightboxPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lightbox || !lightboxProject) return;
    const total = lightboxProject.images.length;
    const newIdx = (lightbox.imageIndex - 1 + total) % total;
    setLightbox({
      projectId: lightbox.projectId,
      imageIndex: newIdx
    });
    setActiveImageIndices(prev => ({
      ...prev,
      [lightbox.projectId]: newIdx
    }));
  };
  const handleLightboxNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lightbox || !lightboxProject) return;
    const total = lightboxProject.images.length;
    const newIdx = (lightbox.imageIndex + 1) % total;
    setLightbox({
      projectId: lightbox.projectId,
      imageIndex: newIdx
    });
    setActiveImageIndices(prev => ({
      ...prev,
      [lightbox.projectId]: newIdx
    }));
  };
  return <section id="nuestros-trabajos" data-nav="dark" className="relative w-full bg-background text-foreground py-20 lg:py-32 overflow-hidden">
      {/* Decorative Technical Grid Lines Background */}
      <div className="pointer-events-none absolute inset-0 opacity-15 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem]" aria-hidden="true" />

      {/* Decorative Ambient Radial Glow */}
      <div className="pointer-events-none absolute -top-40 right-1/4 size-[32rem] rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-12">
        {/* Header Block */}
        <div className="max-w-4xl mx-auto text-center mb-16 lg:mb-20">
          {/* Eyebrow & Brand Tag */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-1.5 text-xs font-mono tracking-widest text-primary uppercase backdrop-blur-md mb-6">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span>{t.eyebrow}</span>
          </motion.div>

          {/* Main Display Heading */}
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.15] mb-6">
            {t.headingPart1} <br className="hidden sm:inline" />
            <span className="text-muted-foreground font-normal">
              {t.headingPart2}
            </span>
          </motion.h2>

          {/* Locations Bar */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm text-muted-foreground font-medium">
            <MapPin className="size-4 text-primary shrink-0" />
            {LOCATIONS.map((loc, idx) => <React.Fragment key={loc}>
                <span className="hover:text-foreground transition-colors">{loc}</span>
                {idx < LOCATIONS.length - 1 && <span className="text-border font-mono">•</span>}
              </React.Fragment>)}
          </motion.div>
        </div>

        {/* Experience Trust Banner Block */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-12 rounded-2xl border border-white/10 bg-card/80 p-6 sm:p-8 backdrop-blur-md shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="flex size-14 sm:size-16 items-center justify-center rounded-xl bg-secondary border border-white/10 text-primary shrink-0 font-mono text-2xl font-bold">
              +15
            </div>
            <div>
              <p className="font-mono text-xs text-primary uppercase tracking-wider font-semibold">
                {t.bannerYearsLabel}
              </p>
              <h3 className="font-sans text-xl sm:text-2xl font-bold text-foreground">
                {t.bannerTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t.bannerParagraph}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0 justify-end">
            <div className="flex items-center gap-2 rounded-lg bg-secondary/80 px-3.5 py-2 text-xs font-mono text-foreground border border-white/5">
              <Shield className="size-4 text-primary" />
              <span>{t.bannerBadge1}</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-secondary/80 px-3.5 py-2 text-xs font-mono text-foreground border border-white/5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>{t.bannerBadge2}</span>
            </div>
          </div>
        </motion.div>

        {/* Category Filters */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {categoryFilters.map(cat => <Button key={cat.key} onClick={() => setActiveCategory(cat.key)} className={`px-4 py-2 rounded-full text-xs sm:text-sm font-mono transition-all duration-200 cursor-pointer ${activeCategory === cat.key ? "bg-primary text-primary-foreground font-bold shadow-[0_0_15px_rgba(59,130,246,0.25)]" : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground border border-white/5"}`}>
              {cat.label}
            </Button>)}
        </div>

        {/* Projects Grid: 2 items side by side, 24px gap */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filteredProjects.map((project, i) => {
          const activeIdx = activeImageIndices[project.id] || 0;
          const currentImg = project.images[activeIdx] || project.images[0];
          const hasMultipleImages = project.images.length > 1;
          const projectTrans = t.projects[project.id as keyof typeof t.projects] || {
            title: project.id,
            category: ""
          };
          return <motion.div key={project.id} variants={itemVariants} data-index={i}>
                <Card className="group relative h-full overflow-hidden rounded-xl border border-white/10 bg-card transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] flex flex-col justify-between">
                  {/* Image Aspect Ratio Wrapper (Clickable to open lightbox) */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-black cursor-pointer" onClick={() => setLightbox({
                projectId: project.id,
                imageIndex: activeIdx
              })}>
                    <img src={currentImg.src} data-wvc-srcset={currentImg.srcset} data-wvc-sizes={currentImg.sizes} alt={currentImg.alt} loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105" />
                    {/* Subtle Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent opacity-90 pointer-events-none" />

                    {/* Image Carousel Navigation Controls */}
                    {hasMultipleImages && <>
                        <Button type="button" variant="ghost" size="icon" onClick={e => handlePrevImage(e, project.id, project.images.length)} aria-label={t.ariaPrev} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 size-8 rounded-full bg-black/50 text-white backdrop-blur-xs transition-colors hover:bg-black/80 hover:text-white cursor-pointer p-0">
                          <ChevronLeft className="size-5" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={e => handleNextImage(e, project.id, project.images.length)} aria-label={t.ariaNext} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 size-8 rounded-full bg-black/50 text-white backdrop-blur-xs transition-colors hover:bg-black/80 hover:text-white cursor-pointer p-0">
                          <ChevronRight className="size-5" />
                        </Button>
                        <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
                          <span className="inline-flex items-center rounded-md bg-background/80 backdrop-blur-md border border-white/10 px-2 py-0.5 font-mono text-xs text-foreground font-semibold shadow-xs">
                            {activeIdx + 1}/{project.images.length}
                          </span>
                        </div>
                      </>}

                    {/* Top Badges */}
                    <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-2 pointer-events-none">
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-background/80 backdrop-blur-md border border-white/10 px-3 py-1 font-mono text-xs font-semibold text-primary">
                        <Zap className="size-3" />
                        {projectTrans.category}
                      </span>
                      <span className="rounded-md bg-background/80 backdrop-blur-md border border-white/10 px-2.5 py-1 font-mono text-xs text-muted-foreground">
                        {project.year}
                      </span>
                    </div>
                  </div>

                  {/* Card Info Content */}
                  <CardContent className="p-6 flex flex-col justify-between flex-grow">
                    <div className="mb-4">
                      <h3 className="font-sans text-xl sm:text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-200">
                        {projectTrans.title}
                      </h3>
                    </div>

                    <div className="pt-4 border-t border-border/50 flex items-center justify-between">
                      <span className="text-xs font-mono text-muted-foreground">
                        {t.cardGuarantee}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-primary group-hover:translate-x-1 transition-transform duration-200">
                        {t.cardDetails} <ArrowUpRight className="size-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-16 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild className="w-full sm:w-auto rounded-full bg-[#25D366] text-white font-bold px-7 py-3.5 hover:bg-[#25D366]/90 transition-all cursor-pointer">
            <Link to="https://wa.me/34662498828" newTab>
              {t.ctaWhatsapp}
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full sm:w-auto rounded-full border-white/15 bg-secondary text-foreground hover:bg-white/10 transition-all cursor-pointer">
            <Link to="#contacto">
              {t.ctaQuote}
            </Link>
          </Button>
        </motion.div>
      </div>

      {/* Fullscreen Interactive Lightbox Overlay */}
      <AnimatePresence>
        {lightbox && lightboxProject && lightboxImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-4 sm:p-6 backdrop-blur-md" onClick={() => setLightbox(null)}>
            {/* Close Button */}
            <Button type="button" variant="ghost" size="icon" onClick={e => {
          e.stopPropagation();
          setLightbox(null);
        }} aria-label={t.ariaClose} className="absolute top-4 right-4 z-20 size-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer">
              <X className="size-6" />
            </Button>

            {/* Lightbox Main Box */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.2,
          ease: "easeOut"
        }} className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
              <img src={lightboxImage.src} data-wvc-srcset={lightboxImage.srcset} data-wvc-sizes={lightboxImage.sizes} alt={lightboxImage.alt} className="max-h-[75vh] max-w-[90vw] object-contain rounded-lg shadow-2xl select-none" />

              {/* Title & Caption */}
              <div className="mt-3 text-center max-w-xl">
                <p className="text-base font-bold text-white">
                  {t.projects[lightboxProject.id as keyof typeof t.projects]?.title || lightboxProject.id}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{lightboxImage.alt}</p>
              </div>

              {/* Album Multi-image Navigation */}
              {lightboxProject.images.length > 1 && <>
                  <Button type="button" variant="ghost" size="icon" onClick={handleLightboxPrev} aria-label={t.ariaPrev} className="absolute left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-20 size-10 sm:size-12 rounded-full bg-black/70 text-white border border-white/20 backdrop-blur-md hover:bg-primary hover:text-primary-foreground transition-all cursor-pointer p-0">
                    <ChevronLeft className="size-6" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" onClick={handleLightboxNext} aria-label={t.ariaNext} className="absolute right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-20 size-10 sm:size-12 rounded-full bg-black/70 text-white border border-white/20 backdrop-blur-md hover:bg-primary hover:text-primary-foreground transition-all cursor-pointer p-0">
                    <ChevronRight className="size-6" />
                  </Button>
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-white/10 border border-white/15 px-3 py-1 font-mono text-xs text-white font-semibold">
                      {lightbox.imageIndex + 1}/{lightboxProject.images.length}
                    </span>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}